import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Tenant, RentEntry, PaymentSubmission } from '@/api/types';
import { PaymentReceiptUploadModal } from './PaymentReceiptUploadModal';
import { Building2, Download, Upload } from 'lucide-react';
import { fmt, useTenantPortalGuard, HostelSelector } from './tenantPortalShared';
import { Select } from '@/components/ui/Select';

type TFunc = ReturnType<typeof useTranslation>['t'];

const RENT_STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PAID:    'bg-green-100 text-green-800',
  PARTIAL: 'bg-blue-100 text-blue-800',
  OVERDUE: 'bg-red-100 text-red-800',
  WAIVED:  'bg-gray-100 text-gray-600',
};

function rentStatusLabels(t: TFunc): Record<string, string> {
  return {
    PENDING: t('rent.pending'),
    PAID:    t('rent.paid'),
    PARTIAL: t('rent.partial'),
    OVERDUE: t('rent.overdue'),
    WAIVED:  t('rent.waived'),
  };
}

// ── Invoice builder ────────────────────────────────────────────────────────────
function fmtMoney(n: number) {
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function buildInvoiceHtml(tenant: Tenant, entry: RentEntry, t: TFunc) {
  const hostelName  = tenant.hostelName    ?? t('tenant.invoiceHostelFallback', 'Hostel');
  const hostelAddr  = tenant.hostelAddress ?? '';
  const hostelPhone = tenant.hostelPhone   ?? '';
  const receiptNo   = `REC-${entry.id.slice(0, 8).toUpperCase()}`;
  const remaining   = Math.max(0, Number(entry.amountDue) - Number(entry.amountPaid));
  const statusColor = entry.status === 'PAID' ? '#065f46' : entry.status === 'OVERDUE' ? '#991b1b' : '#92400e';
  const statusBg    = entry.status === 'PAID' ? '#d1fae5' : entry.status === 'OVERDUE' ? '#fee2e2' : '#fef3c7';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Invoice – ${tenant.fullName} – ${entry.billingMonth}</title>
<style>
  @page{size:A4;margin:18mm 20mm}*{box-sizing:border-box;margin:0;padding:0;font-family:Arial,sans-serif}
  body{color:#111;font-size:13px}.center{text-align:center}
  .hostel-name{font-size:22px;font-weight:700;color:#1E3A5F}.sub{font-size:12px;color:#555;margin-top:3px}
  .title{font-size:16px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#0f766e;margin-top:12px}
  .receipt-no{display:inline-block;margin-top:6px;padding:3px 14px;border:1px solid #99f6e4;border-radius:20px;background:#f0fdfa;font-size:11px;color:#0f766e;font-weight:600}
  hr{border:none;border-top:1px solid #e5e7eb;margin:14px 0}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:10px 30px;margin-bottom:18px}
  .fl{font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.5px;margin-bottom:2px}
  .fv{font-size:13px;font-weight:600;color:#111}
  table{width:100%;border-collapse:collapse;border:1px solid #e5e7eb}
  thead tr{background:#f8fafc}th{padding:10px 16px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;font-weight:600}
  th:last-child{text-align:right}td{padding:10px 16px;font-size:13px;border-top:1px solid #e5e7eb}td:last-child{text-align:right;font-weight:500}
  .row-remaining td{background:${remaining>0?'#fff1f2':'#f0fdf4'};font-weight:700;font-size:14px;border-top:2px solid ${remaining>0?'#fca5a5':'#86efac'}}
  .row-remaining td:last-child{color:${remaining>0?'#dc2626':'#16a34a'}}
  .badge{display:inline-block;padding:3px 12px;border-radius:20px;font-size:11px;font-weight:700;background:${statusBg};color:${statusColor}}
  .sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:30px;margin-top:50px}
  .sig-box{border-bottom:1px solid #6b7280;min-height:30px}.sig-label{font-size:10px;color:#9ca3af;margin-top:4px}
  .footer{margin-top:30px;text-align:center;font-size:11px;color:#9ca3af}
</style></head><body>
<div class="center">
  <p class="hostel-name">${hostelName}</p>
  ${hostelAddr?`<p class="sub">${hostelAddr}</p>`:''}
  ${hostelPhone?`<p class="sub">${t('tenant.invoiceTel', 'Tel:')} ${hostelPhone}</p>`:''}
  <p class="title">${t('tenant.invoiceTitle', 'Rent Invoice')}</p><span class="receipt-no">${receiptNo}</span>
</div><hr/>
<div class="grid2">
  <div><p class="fl">${t('tenant.invoiceTenantName', 'Tenant Name')}</p><p class="fv">${tenant.fullName}</p></div>
  <div><p class="fl">${t('common.phone')}</p><p class="fv">${tenant.phone}</p></div>
  <div><p class="fl">${t('common.room')}</p><p class="fv">${tenant.roomNumber??'—'}</p></div>
  <div><p class="fl">${t('common.bed')}</p><p class="fv">${tenant.bedLabel??'—'}</p></div>
  <div><p class="fl">${t('rent.billingMonth')}</p><p class="fv">${entry.billingMonth}</p></div>
  <div><p class="fl">${t('rent.dueDate')}</p><p class="fv">${entry.dueDate??'—'}</p></div>
  <div><p class="fl">${t('common.status')}</p><p class="fv"><span class="badge">${entry.status}</span></p></div>
  <div><p class="fl">${t('tenant.invoiceGenerated', 'Generated')}</p><p class="fv">${new Date().toLocaleDateString('en-IN')}</p></div>
</div>
<table><thead><tr><th>${t('complaint.description')}</th><th>${t('common.amount')}</th></tr></thead><tbody>
  <tr><td>${t('tenant.invoiceMonthlyRent', 'Monthly Rent — {{month}}', { month: entry.billingMonth })}</td><td>${fmtMoney(Number(entry.amountDue))}</td></tr>
  <tr><td>${t('rent.amountPaid')}</td><td style="color:#059669">${fmtMoney(Number(entry.amountPaid))}</td></tr>
  <tr class="row-remaining"><td>${remaining>0?t('tenant.invoiceBalanceDue', 'Balance Due'):t('tenant.invoiceFullyPaid', 'Fully Paid ✔')}</td><td>${fmtMoney(remaining)}</td></tr>
</tbody></table>
<div class="sig-grid">
  <div><div class="sig-box"></div><p class="sig-label">${t('tenant.invoiceTenantSignature', 'Tenant Signature')}</p></div>
  <div><div class="sig-box"></div><p class="sig-label">${t('tenant.invoiceAuthSignature', 'Authorised Signature')}</p></div>
</div>
<p class="footer">${t('tenant.invoiceFooter', 'This is a computer generated invoice — {{hostelName}}', { hostelName })}</p>
</body></html>`;
}

function downloadInvoice(tenant: Tenant, entry: RentEntry, t: TFunc) {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(buildInvoiceHtml(tenant, entry, t));
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
}

export function TenantPaymentsPage() {
  const { t } = useTranslation();
  const portal = useTenantPortalGuard();
  const [uploadingEntry, setUploadingEntry] = useState<RentEntry | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const { data: rent = [], isLoading: rentLoading } = useQuery<RentEntry[]>({
    queryKey: ['tenant-portal-rent'],
    queryFn: () => client.get('/api/tenant-portal/rent').then(r => r as unknown as RentEntry[]),
  });

  const { data: paymentSubmissions = [] } = useQuery<PaymentSubmission[]>({
    queryKey: ['tenant-portal-payment-submissions'],
    queryFn: () => client.get('/api/tenant-portal/payment-submissions').then(r => r as unknown as PaymentSubmission[]),
  });

  const statusLabels = rentStatusLabels(t);

  if ('guard' in portal) return portal.guard;
  const { tenant, activeTenants, tenantIdx, setSelectedTenantIdx } = portal;

  const tenantRent = rent.filter(e => e.tenantId === tenant.id);
  const filteredRent = statusFilter ? tenantRent.filter(e => e.status === statusFilter) : tenantRent;

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{t('tenant.payments', 'Payments')}</h1>
        {tenantRent.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t('supervisor.paymentStatus', 'Payment Status')}</span>
            <Select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-auto"
              options={[
                { value: '', label: t('common.all') },
                ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
              ]}
            />
          </div>
        )}
      </div>

      <HostelSelector activeTenants={activeTenants} tenantIdx={tenantIdx} onChange={setSelectedTenantIdx} />
      {activeTenants.length <= 1 && (
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm text-sm">
          <Building2 size={14} className="text-amber-600 shrink-0" />
          <span className="text-xs text-gray-500">{t('tenant.hostelLabel', 'Hostel:')}</span>
          <span className="font-medium text-gray-700">
            {tenant.hostelName ?? '—'}{tenant.roomNumber ? ` · ${t('common.room')} ${tenant.roomNumber}` : ''}
          </span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {rentLoading ? (
          <div className="p-10 text-center">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400 text-sm">{t('common.loading')}</p>
          </div>
        ) : tenantRent.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">{t('rent.noEntriesYet', 'No rent entries yet.')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">{t('rent.billingMonth')}</th>
                  <th className="px-4 py-3 text-left">{t('rent.amountDue')}</th>
                  <th className="px-4 py-3 text-left">{t('rent.amountPaid')}</th>
                  <th className="px-4 py-3 text-left">{t('common.status')}</th>
                  <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRent.map(e => {
                  const pendingSubmission = paymentSubmissions.find(s => s.rentEntryId === e.id && s.status === 'PENDING');
                  return (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{e.billingMonth}</td>
                      <td className="px-4 py-3 text-gray-600">{fmt(e.amountDue)}</td>
                      <td className="px-4 py-3 text-gray-600">{fmt(e.amountPaid)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RENT_STATUS_COLORS[e.status] ?? 'bg-gray-100 text-gray-600'}`}>
                          {statusLabels[e.status] ?? e.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {e.status === 'PAID' && (
                          <button
                            title={t('tenant.downloadInvoiceTooltip', 'Download Invoice')}
                            onClick={() => downloadInvoice(tenant, e, t)}
                            className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 border border-teal-200 hover:bg-teal-50 px-2 py-1 rounded-lg transition"
                          >
                            <Download size={12} /> {t('tenant.invoiceButtonLabel', 'Invoice')}
                          </button>
                        )}
                        {e.status !== 'PAID' && pendingSubmission && (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
                            {t('tenant.pendingVerification', 'Pending Verification')}
                          </span>
                        )}
                        {e.status !== 'PAID' && !pendingSubmission && (
                          <button
                            title={t('tenant.uploadReceiptTooltip', 'Upload Payment Receipt')}
                            onClick={() => setUploadingEntry(e)}
                            className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 border border-amber-200 hover:bg-amber-50 px-2 py-1 rounded-lg transition"
                          >
                            <Upload size={12} /> {t('tenant.uploadReceipt', 'Upload Receipt')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredRent.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-10">{t('common.noData')}</p>
            )}
          </div>
        )}
      </div>

      {uploadingEntry && (
        <PaymentReceiptUploadModal entry={uploadingEntry} onClose={() => setUploadingEntry(null)} />
      )}
    </div>
  );
}
